import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Alert } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { AuthContext } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import { PanelCard, Kicker, AmberButton, EngGrid, StatusPill } from '../components/design';

const MONO = "'IBM Plex Mono', monospace";

const GhostBtn = ({ children, onClick, sx = {} }) => (
  <Box
    component="button"
    onClick={onClick}
    sx={{
      background: 'transparent',
      border: '1px solid rgba(120,150,200,.28)',
      color: '#cdd9ee',
      fontWeight: 500,
      fontSize: '13px',
      px: '16px',
      py: '10px',
      borderRadius: '9px',
      cursor: 'pointer',
      fontFamily: "'Lexend', sans-serif",
      whiteSpace: 'nowrap',
      transition: 'border-color .15s, color .15s',
      '&:hover': { borderColor: 'rgba(45,212,191,.5)', color: '#fff' },
      ...sx,
    }}
  >
    {children}
  </Box>
);

const SpecCell = ({ label, value }) => (
  <Box sx={{ background: '#0c1830', p: '13px 15px' }}>
    <Box sx={{ fontFamily: MONO, fontSize: '10px', letterSpacing: '.1em', textTransform: 'uppercase', color: '#7f93b8', mb: '6px' }}>{label}</Box>
    <Box sx={{ fontFamily: MONO, fontSize: '13.5px', color: '#dce6fa' }}>{value || 'N/A'}</Box>
  </Box>
);

const Profile = () => {
  const { user, isAuthenticated, loading: authLoading, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (authLoading) return;
    const fetchSubscriptionData = async () => {
      try {
        setLoading(true);
        const api = await import('../utils/api').then(mod => mod.default);
        const response = await api.get('/api/payments/subscription-status');
        const data = response.data;

        let formattedSubscriptions = [];
        if (data?.subscription) {
          formattedSubscriptions.push({
            subscriptionId: data.subscription.id || data.subscriptionId,
            subscriptionStatus: data.status || data.subscription.status,
            subscriptionPlan: data.subscription.plan?.nickname || data.subscription.product?.name || '',
            amount: data.subscription.plan?.amount || 0,
            currency: data.subscription.plan?.currency || 'USD',
            interval: data.subscription.plan?.interval || 'year',
            startDate: data.subscription.current_period_start || null,
            expiryDate: data.subscription.current_period_end || null,
            createdAt: data.subscription.created || null,
            product: data.subscription.product ? { name: data.subscription.product.name, description: data.subscription.product.description } : null,
            paymentMethod: data.subscription.payment_method ? { brand: data.subscription.payment_method.brand, last4: data.subscription.payment_method.last4 } : null,
          });
        } else if (data?.subscriptionId) {
          formattedSubscriptions.push({
            subscriptionId: data.subscriptionId,
            subscriptionStatus: data.status || 'unknown',
            subscriptionDescription: data.description,
            amount: data.amount || 0,
            currency: data.currency || 'USD',
          });
        }
        setSubscriptions(formattedSubscriptions);
      } catch (err) {
        console.error('Error fetching subscription information:', err);
        setError('Failed to load subscription information. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    if (isAuthenticated) fetchSubscriptionData();
  }, [isAuthenticated, authLoading]);

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A';
  const formatCurrency = (amount, currency = 'USD') => amount
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 0 }).format(amount / 100)
    : 'N/A';

  const initial = user?.username?.charAt(0).toUpperCase() || '?';
  const sub = subscriptions[0] || null;
  const isActive = sub?.subscriptionStatus === 'active';

  if (authLoading || loading) {
    return (
      <>
        <Navbar />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', background: '#0a1326' }}>
          <CircularProgress sx={{ color: '#2dd4bf' }} />
        </Box>
      </>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: '#0a1326' }}>
      <Navbar />

      <Box sx={{ position: 'relative', maxWidth: '760px', mx: 'auto', px: '28px', pt: '40px', pb: '80px' }}>
        <EngGrid opacity={0.4} sx={{ inset: '0 0 auto 0', height: '230px' }} animate={false} />

        {/* Header */}
        <Box sx={{ position: 'relative', mb: '30px' }}>
          <Kicker variant="plain" sx={{ mb: '10px' }}>account</Kicker>
          <Box component="h1" sx={{ m: 0, fontSize: '34px', fontWeight: 700, letterSpacing: '-.02em', color: '#f4f8ff' }}>
            Your profile
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Identity card */}
          <PanelCard label="ACCOUNT">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '22px',
                p: '22px',
                flexWrap: 'wrap',
                '@media (max-width:600px)': { flexDirection: 'column', alignItems: 'stretch' },
              }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  width: 52,
                  height: 52,
                  borderRadius: '13px',
                  background: 'rgba(45,212,191,.14)',
                  border: '1px solid rgba(45,212,191,.45)',
                  color: '#2dd4bf',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600,
                  fontSize: '21px',
                }}
              >
                {initial}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ fontSize: '17px', fontWeight: 600, color: '#f4f8ff', lineHeight: 1.2 }}>{user?.username || '—'}</Box>
                <Box sx={{ fontFamily: MONO, fontSize: '13px', color: '#9fb2d4', mt: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user?.email || '—'}
                </Box>
              </Box>
              <Box sx={{ flexShrink: 0, display: 'flex', gap: '9px' }}>
                <GhostBtn onClick={() => { logout(); navigate('/'); }}>
                  Sign out
                </GhostBtn>
              </Box>
            </Box>
          </PanelCard>

          {error && <Alert severity="error" sx={{ background: 'rgba(244,113,106,.1)', color: '#f4a39e', border: '1px solid rgba(244,113,106,.3)' }}>{error}</Alert>}

          {/* Subscription card */}
          <Box
            sx={{
              border: '1px solid rgba(120,150,200,.22)',
              borderRadius: '14px',
              background: 'linear-gradient(180deg,#0e1c39,#0a1326)',
              boxShadow: '0 24px 60px rgba(0,0,0,.3)',
              overflow: 'hidden',
            }}
          >
            {/* Header row */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '18px', py: '13px', borderBottom: '1px solid rgba(120,150,200,.18)', background: 'rgba(8,15,30,.5)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', fontFamily: MONO, fontSize: '11.5px', letterSpacing: '.1em', color: '#90a6cc' }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: isActive ? '#44d07b' : '#8298bd', flexShrink: 0 }} />
                SUBSCRIPTION
              </Box>
              {isActive
                ? <StatusPill status="active" pulse />
                : <StatusPill status="inactive" label="NO ACTIVE PLAN" />
              }
            </Box>

            <Box sx={{ p: '24px 22px' }}>
              {isActive && sub ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px', mb: '22px' }}>
                    <Box>
                      <Box sx={{ fontSize: '18px', fontWeight: 600, color: '#f4f8ff', mb: '4px' }}>
                        {sub.subscriptionPlan || 'Annual — all features'}
                      </Box>
                      <Box sx={{ fontFamily: MONO, fontSize: '12px', color: '#7f93b8' }}>one user · unlimited drawings</Box>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '5px', justifyContent: 'flex-end' }}>
                        <Box sx={{ fontFamily: MONO, fontSize: '30px', fontWeight: 600, color: '#f4f8ff', lineHeight: 1 }}>
                          {formatCurrency(sub.amount, sub.currency)}
                        </Box>
                        <Box sx={{ fontFamily: MONO, fontSize: '13px', color: '#8298bd', whiteSpace: 'nowrap' }}>
                          / {sub.interval || 'yr'}
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {/* Spec grid */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1px', background: 'rgba(120,150,200,.14)', border: '1px solid rgba(120,150,200,.14)', borderRadius: '10px', overflow: 'hidden' }}>
                    <SpecCell label="Started" value={formatDate(sub.startDate || sub.createdAt)} />
                    <SpecCell label="Renews" value={formatDate(sub.expiryDate)} />
                    <SpecCell
                      label="Payment"
                      value={sub.paymentMethod ? `${sub.paymentMethod.brand ? sub.paymentMethod.brand.charAt(0).toUpperCase() + sub.paymentMethod.brand.slice(1) : ''} •••• ${sub.paymentMethod.last4 || ''}` : null}
                    />
                    <SpecCell label="Subscription" value={sub.subscriptionId ? sub.subscriptionId.slice(0, 12) + '…' : null} />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', mt: '18px', flexWrap: 'wrap' }}>
                    <GhostBtn>Manage billing</GhostBtn>
                    <GhostBtn>Download invoice</GhostBtn>
                    <Box
                      component="button"
                      sx={{ ml: 'auto', background: 'transparent', border: 'none', color: '#7f93b8', fontWeight: 500, fontSize: '12.5px', p: '10px 4px', cursor: 'pointer', fontFamily: "'Lexend', sans-serif", '&:hover': { color: '#f4a39e' } }}
                    >
                      Cancel plan
                    </Box>
                  </Box>
                </>
              ) : (
                <Box>
                  <Box sx={{ fontSize: '15px', color: '#aebfdd', mb: '20px', lineHeight: 1.6 }}>
                    You don't have an active subscription. Subscribe now to access all features.
                  </Box>
                  <AmberButton onClick={() => navigate('/checkout')}>Subscribe now <Box component="span" sx={{ fontFamily: MONO }}>→</Box></AmberButton>
                </Box>
              )}
            </Box>
          </Box>

          {/* Billing history */}
          {subscriptions.length > 0 && (
            <Box
              sx={{
                border: '1px solid rgba(120,150,200,.22)',
                borderRadius: '14px',
                background: 'linear-gradient(180deg,#0d1a33,#0a1326)',
                boxShadow: '0 24px 60px rgba(0,0,0,.3)',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '18px', py: '13px', borderBottom: '1px solid rgba(120,150,200,.18)', background: 'rgba(8,15,30,.5)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', fontFamily: MONO, fontSize: '11.5px', letterSpacing: '.1em', color: '#90a6cc' }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#2dd4bf', flexShrink: 0 }} />
                  BILLING HISTORY
                </Box>
                <Box sx={{ fontFamily: MONO, fontSize: '11px', color: '#5f7398' }}>{subscriptions.length} record{subscriptions.length !== 1 ? 's' : ''}</Box>
              </Box>

              {/* Table header */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1.1fr .9fr .8fr 1fr auto', gap: '18px', alignItems: 'center', px: '18px', py: '11px', borderBottom: '1px solid rgba(120,150,200,.18)', background: 'rgba(8,15,30,.3)', '@media (max-width:760px)': { gridTemplateColumns: '1fr auto' } }}>
                <Box sx={{ fontFamily: MONO, fontSize: '10px', letterSpacing: '.12em', textTransform: 'uppercase', color: '#5f7398' }}>Status</Box>
                <Box sx={{ fontFamily: MONO, fontSize: '10px', letterSpacing: '.12em', textTransform: 'uppercase', color: '#5f7398', display: { xs: 'none', md: 'block' } }}>Plan</Box>
                <Box sx={{ fontFamily: MONO, fontSize: '10px', letterSpacing: '.12em', textTransform: 'uppercase', color: '#5f7398' }}>Amount</Box>
                <Box sx={{ fontFamily: MONO, fontSize: '10px', letterSpacing: '.12em', textTransform: 'uppercase', color: '#5f7398', display: { xs: 'none', md: 'block' } }}>Date</Box>
                <Box />
              </Box>

              {subscriptions.map((s, i) => (
                <Box
                  key={s.subscriptionId || i}
                  sx={{ display: 'grid', gridTemplateColumns: '1.1fr .9fr .8fr 1fr auto', gap: '18px', alignItems: 'center', px: '18px', py: '13px', borderBottom: i < subscriptions.length - 1 ? '1px solid rgba(120,150,200,.12)' : 'none', '@media (max-width:760px)': { gridTemplateColumns: '1fr auto' }, '&:hover': { background: 'rgba(120,150,200,.04)' } }}>
                  <Box><StatusPill status={s.subscriptionStatus === 'active' ? 'paid' : s.subscriptionStatus} label={s.subscriptionStatus?.toUpperCase()} /></Box>
                  <Box sx={{ fontSize: '13.5px', color: '#dce6fa', display: { xs: 'none', md: 'block' } }}>{s.subscriptionPlan || 'Annual — all features'}</Box>
                  <Box sx={{ fontFamily: MONO, fontSize: '13px', color: '#eaf0fb' }}>{formatCurrency(s.amount, s.currency)} <Box component="span" sx={{ color: '#5f7398' }}>/ {s.interval || 'yr'}</Box></Box>
                  <Box sx={{ fontFamily: MONO, fontSize: '13px', color: '#9fb2d4', display: { xs: 'none', md: 'block' } }}>{formatDate(s.startDate || s.createdAt)}</Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <GhostBtn sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px', py: '7px', px: '13px', fontSize: '12.5px' }}>
                      <DownloadIcon sx={{ fontSize: '13px' }} /> Invoice
                    </GhostBtn>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Profile;
